import { Form } from 'formik';
import { assign, pick, keys } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import {
  DialogProps,
  FormControls,
  FormikTextField,
  FormikDialog,
} from 'components';
import { TranslationNamespace } from 'i18n';
import { TelegramUser } from 'types';

type Props = {
  data?: TelegramUser;
} & DialogProps;

export const TelegramUserDetailsDialog: React.FC<Props> = ({
  data,
  ...rest
}) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'features.telegram_flow.telegram_users.dialog',
  });
  const { t: tCommon } = useTranslation();

  const title = useMemo(
    () => (data ? t('edit_title') : t('create_title')),
    [t, data],
  );

  const getInitialValues = useCallback(
    () => ({
      name: data?.name || '',
      phone: data?.phone || '',
    }),
    [data],
  );

  const [initialValues, setInitialValues] = useState(getInitialValues());

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string().required(tCommon('errors.required')),
        phone: Yup.string().required(tCommon('errors.required')),
      }),
    [tCommon],
  );

  useEffect(() => {
    const _initialValues = getInitialValues();
    setInitialValues(assign(_initialValues, pick(data, keys(_initialValues))));
  }, [data, getInitialValues]);

  return (
    <FormikDialog
      dialogProps={{
        title,
        ...rest,
      }}
      formikProps={{
        initialValues,
        enableReinitialize: true,
        validationSchema,
      }}
    >
      {() => (
        <Form>
          <FormControls>
            <FormikTextField name="name" label={t('fields.name')} required />
            <FormikTextField
              name="phone"
              label={t('fields.phone')}
              required
              disabled={!!data}
            />
          </FormControls>
        </Form>
      )}
    </FormikDialog>
  );
};
